import { useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'
import { useApi } from '../lib/useApi'

interface JobResult {
  id: string; jobNumber: string; status: string
  customer: { name: string }
  vehicle: { registration: string; make: string; model: string }
}
interface CustomerResult {
  id: string; name: string; phone: string
}

interface Props {
  onNavigateJobs: (search: string) => void
}

export function GlobalSearch({ onNavigateJobs }: Props) {
  const api = useApi()
  const [query, setQuery] = useState('')
  const [jobs, setJobs] = useState<JobResult[]>([])
  const [customers, setCustomers] = useState<CustomerResult[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!query.trim()) { setJobs([]); setCustomers([]); setOpen(false); return }

    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const [jobRes, custRes] = await Promise.all([
          api.get<{ items: JobResult[] }>(`/v1/job-cards?q=${encodeURIComponent(query)}&limit=5`),
          api.get<{ items: CustomerResult[] }>(`/v1/customers?q=${encodeURIComponent(query)}&limit=5`),
        ])
        setJobs(jobRes.items)
        setCustomers(custRes.items)
        setOpen(true)
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }, 300)
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node))
        setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  const hasResults = jobs.length > 0 || customers.length > 0

  function pickJob(job: JobResult) {
    setQuery(''); setOpen(false)
    onNavigateJobs(job.jobNumber)
  }

  function pickCustomer(c: CustomerResult) {
    setQuery(''); setOpen(false)
    onNavigateJobs(c.name)
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--bg-2)', borderRadius: 8, padding: '0 12px', height: 36,
      }}>
        {loading
          ? <Icon name="loader" size={16} style={{ color: 'var(--fg-3)', animation: 'spin 1s linear infinite' }} />
          : <Icon name="search" size={16} style={{ color: 'var(--fg-3)' }} />
        }
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (hasResults) setOpen(true) }}
          onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery('') } }}
          placeholder="Search jobs, customers, reg numbers…"
          style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--fg-1)' }}
        />
        {query && (
          <button onClick={() => { setQuery(''); setOpen(false) }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--fg-3)', display: 'flex' }}>
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {open && hasResults && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#fff', borderRadius: 10, border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--shadow-md)', zIndex: 200, overflow: 'hidden',
        }}>
          {jobs.length > 0 && (
            <>
              <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Job cards
              </div>
              {jobs.map(j => (
                <button key={j.id} onClick={() => pickJob(j)} style={rowStyle}>
                  <Icon name="clipboard" size={15} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{j.jobNumber}</span>
                    <span style={{ fontSize: 12, color: 'var(--fg-3)', marginLeft: 8 }}>{j.customer.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--fg-3)', marginLeft: 8, fontFamily: 'var(--font-mono)' }}>{j.vehicle.registration}</span>
                  </div>
                  <StatusDot status={j.status} />
                </button>
              ))}
            </>
          )}
          {customers.length > 0 && (
            <>
              <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', letterSpacing: '0.06em', textTransform: 'uppercase', borderTop: jobs.length > 0 ? '1px solid var(--border-subtle)' : 'none' }}>
                Customers
              </div>
              {customers.map(c => (
                <button key={c.id} onClick={() => pickCustomer(c)} style={rowStyle}>
                  <Icon name="user" size={15} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{c.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--fg-3)', marginLeft: 8, fontFamily: 'var(--font-mono)' }}>{c.phone}</span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>View jobs</span>
                </button>
              ))}
            </>
          )}
          {!hasResults && !loading && query.trim() && (
            <div style={{ padding: '16px 14px', fontSize: 13, color: 'var(--fg-3)', textAlign: 'center' }}>
              No results for "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '9px 14px', background: 'none',
  border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  transition: 'background 150ms ease-out',
}

const STATUS_COLORS: Record<string, string> = {
  Intake: '#6B7280', 'In Progress': '#D96E1A', Ready: '#059669', Complete: '#1A2E4A', Paid: '#027A48',
}

function StatusDot({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? '#6B7280'
  return <span style={{ width: 7, height: 7, borderRadius: 999, background: color, flexShrink: 0 }} />
}
